import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { getToken } from '../lib/auth'

export type CreatorPlan = 'none' | 'creator' | 'creator_pro'

export type CreatorOverview = {
  creator: {
    plan: CreatorPlan
    plan_active: boolean
    current_period_end: string | null
    payouts_enabled: boolean
    stripe_connect_account_id: string
  }
  earnings: {
    total_usd: string
    available_usd: string
    available_to_withdraw_usd?: string
    vesting_usd: string
    earned_not_withdrawable_usd?: string
    sources?: {
      gifts_usd: string
      pro_jobs_usd: string
      subscriptions_usd: string
      store_usd: string
    }
    next_vesting_unlock_at: string | null
  }
  withdraw_gate?: {
    ok: boolean
    reason_code: 'OK' | 'FROZEN' | 'WITHDRAW_HOLD' | 'INSUFFICIENT_SETTLED' | string
    message: string | null
    available_to_withdraw_pixe: string
    pending_pixe: string
    withdraw_hold_until: string | null
    risk_level: 'LOW' | 'MEDIUM' | 'HIGH' | null
    frozen: boolean
    frozen_reason: string | null
  }
}

export function planBadge(plan: CreatorPlan) {
  if (plan === 'creator_pro') return { label: 'Creator Pro', cls: 'bg-amber-500/10 text-amber-200 border-amber-500/25' }
  if (plan === 'creator') return { label: 'Creator', cls: 'bg-emerald-500/10 text-emerald-200 border-emerald-500/25' }
  return { label: 'User', cls: 'bg-zinc-700/30 text-zinc-200 border-zinc-600/30' }
}

export function planMeta(plan: CreatorPlan) {
  switch (plan) {
    case 'creator':
      return { price: 10, vestMonths: 12, includedPosts: 10, unlimitedPosts: false }
    case 'creator_pro':
      return { price: 25, vestMonths: 6, includedPosts: Infinity, unlimitedPosts: true }
    default:
      return { price: 0, vestMonths: 0, includedPosts: 0, unlimitedPosts: false }
  }
}

export function money(n: number) {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

export function formatDateShort(isoOrNull: string | null): string {
  if (!isoOrNull) return ''
  const d = new Date(isoOrNull)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function toNum(s: string) {
  const n = Number(s)
  return Number.isFinite(n) ? n : 0
}

export function useCreatorTools() {
  const [data, setData] = useState<CreatorOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [withdrawError, setWithdrawError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setData(null)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<CreatorOverview>('/api/creator/overview/')
      setData(res.data)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load creator overview')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const plan: CreatorPlan = data?.creator?.plan ?? 'none'
  const status = useMemo(() => planBadge(plan), [plan])
  const meta = useMemo(() => planMeta(plan), [plan])

  const earnings = useMemo(() => {
    const totalUsd = toNum(data?.earnings?.total_usd ?? '0')
    const availableUsd = toNum(data?.earnings?.available_to_withdraw_usd ?? data?.earnings?.available_usd ?? '0')
    const vestingUsd = toNum(data?.earnings?.vesting_usd ?? '0')
    const earnedNotWithdrawableUsd = toNum(data?.earnings?.earned_not_withdrawable_usd ?? '0')
    const nextUnlock = data?.earnings?.next_vesting_unlock_at ?? null
    return {
      totalUsd,
      availableUsd,
      vestingUsd,
      earnedNotWithdrawableUsd,
      nextVestingDate: nextUnlock ? formatDateShort(nextUnlock) : '',
    }
  }, [data])

  const payoutsEnabled = Boolean(data?.creator?.payouts_enabled)
  const canWithdraw = plan !== 'none' && payoutsEnabled && earnings.availableUsd > 0.01
  const withdrawGate = useMemo(
    () =>
      data?.withdraw_gate ?? {
        ok: false,
        reason_code: 'INSUFFICIENT_SETTLED',
        message: null,
        available_to_withdraw_pixe: '0',
        pending_pixe: '0',
        withdraw_hold_until: null,
        risk_level: null,
        frozen: false,
        frozen_reason: null,
      },
    [data]
  )

  const devSetPlan = useCallback(
    async (next: CreatorPlan) => {
      await api.post('/api/creator/dev/set-plan/', { plan: next })
      await refresh()
    },
    [refresh]
  )

  const startCheckout = useCallback(
    async (next: Exclude<CreatorPlan, 'none'>) => {
      // Use current origin as return target; backend appends ?session_id=...
      const origin = window.location.origin
      const res = await api.post('/api/creator/checkout/', {
        plan: next,
        success_url: `${origin}/settings/pixe-publish`,
        cancel_url: `${origin}/settings/become-a-creator`,
      })
      const url = res?.data?.checkout_url
      if (typeof url === 'string' && url.startsWith('http')) {
        window.location.assign(url)
        return
      }
      throw new Error('Checkout URL missing')
    },
    []
  )

  const requestWithdraw = useCallback(
    async (pixeAmount: number) => {
      setWithdrawError(null)
      try {
        const res = await api.post('/api/stripe/payouts/request/', { pixe_amount: pixeAmount })
        await refresh()
        return res.data
      } catch (e: any) {
        const payload = e?.response?.data || {}
        if (e?.response?.status === 403) {
          const available = payload?.available_to_withdraw
          const pending = payload?.pending
          const reason = payload?.code || 'WITHDRAW_BLOCKED'
          const msg =
            typeof available === 'number' || typeof pending === 'number'
              ? `${reason}: Available ${available ?? 0} PIXE, Pending ${pending ?? 0} PIXE.`
              : payload?.detail || reason
          setWithdrawError(msg)
        } else {
          setWithdrawError(payload?.detail || e?.message || 'Withdraw failed')
        }
        await refresh()
        throw e
      }
    },
    [refresh]
  )

  return {
    data,
    loading,
    error,
    refresh,
    plan,
    status,
    meta,
    earnings,
    withdrawGate,
    withdrawError,
    payoutsEnabled,
    canWithdraw,
    devSetPlan,
    startCheckout,
    requestWithdraw,
  }
}
